import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { toast } from 'sonner';
import { listAiQueue } from '../endpoints/ai-queue/list_GET.schema';
import { getAiQueueStatus } from '../endpoints/ai-queue/status_GET.schema';
import { enqueueAiTask } from '../endpoints/ai-queue/enqueue_POST.schema';
import { cancelAiTask } from '../endpoints/ai-queue/cancel_POST.schema';

/**
 * Hook to list AI task queue items for a session
 */
export const useAiQueue = (sessionId: string | undefined) => {
  return useQuery({
    queryKey: ['ai-queue', 'list', sessionId],
    queryFn: () => {
      if (!sessionId) throw new Error('Session ID is required');
      return listAiQueue({ sessionId });
    },
    enabled: !!sessionId,
    refetchInterval: 5000, // Poll every 5s to get updates on processing tasks
  });
};

/**
 * Hook to get status of a specific queue item
 */
export const useAiQueueStatus = (queueId: string | undefined) => {
  return useQuery({
    queryKey: ['ai-queue', 'status', queueId],
    queryFn: () => {
      if (!queueId) throw new Error('Queue ID is required');
      return getAiQueueStatus({ queueId });
    },
    enabled: !!queueId,
    refetchInterval: 3000, // Poll more frequently for individual item
  });
};

/**
 * Hook to enqueue a task for AI execution
 */
export const useEnqueueAiTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: enqueueAiTask,
    onSuccess: (data) => {
      toast.success('Task queued for AI execution');
      queryClient.invalidateQueries({ queryKey: ['ai-queue', 'list', data.queueItem.sessionId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list', data.queueItem.sessionId] });
    },
    onError: (error) => {
      toast.error(`Failed to queue task: ${error.message}`);
    },
  });
};

/**
 * Hook to cancel a pending AI task
 */
export const useCancelAiTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelAiTask,
    onSuccess: () => {
      toast.success('AI task cancelled');
      // Invalidate all queue lists since we don't have sessionId
      queryClient.invalidateQueries({ queryKey: ['ai-queue', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
    },
    onError: (error) => {
      toast.error(`Failed to cancel task: ${error.message}`);
    },
  });
};
